import React, { ReactElement } from "react";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";

/**
 * Wraps a control that is locked while an upload is worker-owned (flag
 * REACT_APP_UPLOAD_PENDING_ENABLED, design 14/9 §5.3): a tooltip that says
 * why (a disabled control fires no pointer events, so the tooltip sits on a
 * wrapper - the ProductPendingLock idiom) and ONE on-demand re-read when the
 * user touches it (`onTouch`, throttled by useEditorUploadKindPoll). The
 * disabled descendants get pointer-events: none so the touch reaches the
 * wrapper. When `locked` is false the child is returned AS IS - no wrapper
 * element, no attribute - so the flag-off tree is unchanged.
 */
export function UploadLockedControl({
  locked,
  tooltipKey,
  onTouch,
  block,
  children,
}: {
  locked: boolean;
  tooltipKey?: string;
  onTouch?: () => void;
  // A block-level child (the colour changer) gets a block wrapper.
  block?: boolean;
  children: ReactElement;
}): ReactElement {
  const { t } = useTranslation();
  if (!locked) return children;
  const wrapper = (
    <span
      data-testid="upload-locked-control"
      className={
        (block ? "block w-full" : "inline-flex items-center") +
        " [&_*:disabled]:pointer-events-none"
      }
      onPointerDownCapture={onTouch}
      onFocusCapture={onTouch}
    >
      {children}
    </span>
  );
  if (!tooltipKey) return wrapper;
  return (
    <Tooltip title={t(tooltipKey)} placement="top">
      {wrapper}
    </Tooltip>
  );
}
